/**
 * Date utilities
 *
 * One rule, for every game: a puzzle's "day" is the calendar date on the
 * user's own device. There is no per-game rollover data and no timezone
 * math - reading the device's local date handles DST for free.
 *
 * This is a deliberate simplification (roadmap D23): some games really do
 * derive their date server-side, but where this is wrong the error is
 * bounded to a few hours and only affects scores logged near midnight.
 */

/**
 * The play date to use for anything logged right now: the device's local
 * calendar date as YYYY-MM-DD.
 * @param {Date} [date] Optional instant to read; defaults to now.
 * @returns {string} YYYY-MM-DD in local time
 */
export const getPlayDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Whole-day difference between two YYYY-MM-DD strings (b - a).
 * Parsed as local midnights so the result isn't skewed by the UTC offset.
 * @returns {number}
 */
export const daysBetween = (dateStringA, dateStringB) => {
  const a = new Date(`${dateStringA}T00:00:00`);
  const b = new Date(`${dateStringB}T00:00:00`);
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Whether a string is a well-formed YYYY-MM-DD date.
 * @returns {boolean}
 */
export const isPlayDate = (value) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
